import { tr } from '@/lib/i18n-core';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, money, todayIso } from '@/lib/utils';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { bannerFor, pickCoverage, type AuthorisationSummary } from '../insurance/authorisation';

interface CoverageRow {
  id: string;
  payerId: string;
  plan: { id: string; code: string; name: string } | null;
  policyNo: string;
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
}

export interface InsuredInvoice {
  patient: { id: string } | null;
  payer: { id: string; name: string } | null;
  totals: { netMinor: number; creditMinor: number };
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular text-end">{value}</span>
    </div>
  );
}

function holdLine(summary: AuthorisationSummary): { key: 'x_services_still_need_the_insurers_approval' | 'x_services_waiting_for_the_insurer' | 'the_insurer_has_answered_for_every_service'; count: number; variant: 'warning' | 'default' | 'success' } | null {
  const model = bannerFor(summary);
  if (!model) return null;
  if (model.state === 'needs_send') return { key: 'x_services_still_need_the_insurers_approval', count: model.count, variant: model.variant };
  if (model.state === 'waiting') return { key: 'x_services_waiting_for_the_insurer', count: model.count, variant: model.variant };
  return { key: 'the_insurer_has_answered_for_every_service', count: model.count, variant: model.variant };
}

export function InvoiceInsurancePanel({ invoice, summary }: { invoice: InsuredInvoice; summary: AuthorisationSummary | null }) {
  const { can } = useAuth();
  const payer = invoice.payer;
  const patientId = invoice.patient?.id;
  const coverages = useQuery({
    queryKey: ['patient-coverages', patientId],
    queryFn: () => get<CoverageRow[]>(`/insurance/patients/${patientId}/coverages`),
    enabled: Boolean(patientId && payer) && can('coverage.verify', 'patient.read'),
    staleTime: 60_000,
  });
  if (!payer) return null;

  const coverage = pickCoverage(coverages.data, payer.id, todayIso());
  const hold = summary ? holdLine(summary) : null;
  const channel = summary?.payers.find((row) => row.id === payer.id)?.channel ?? null;
  const panelShare = invoice.totals.creditMinor;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'insurance')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        <Row label={tr('billing', 'payer')} value={payer.name} />
        {coverage ? (
          <>
            <Row label={tr('billing', 'plan')} value={coverage.plan?.name ?? '—'} />
            <Row label={tr('billing', 'policy')} value={<span className="font-mono text-xs">{coverage.policyNo}</span>} />
            <Row label={tr('billing', 'valid_from')} value={coverage.validFrom ? dateOnly(coverage.validFrom) : '—'} />
            <Row label={tr('billing', 'valid_until')} value={coverage.validTo ? dateOnly(coverage.validTo) : '—'} />
          </>
        ) : (
          coverages.isFetched && <p className="text-xs text-muted-foreground">{tr('billing', 'no_policy_on_file_for_this_payer')}</p>
        )}
        <div className="my-2 h-px bg-border" />
        <Row label={tr('billing', 'panel_share')} value={money(panelShare)} />
        <Row label={tr('billing', 'patient_share')} value={money(invoice.totals.netMinor - panelShare)} />
        {hold && (
          <>
            <div className="my-2 h-px bg-border" />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-muted-foreground">{tr('billing', 'authorisation')}</span>
              <Badge variant={hold.variant === 'default' ? 'secondary' : hold.variant}>{tr('billing', hold.key, { count: hold.count })}</Badge>
            </div>
            {channel === 'manual' && <p className="text-xs text-muted-foreground">{tr('billing', 'answered_outside_nphies')}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
